import {
  Box,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { CommitmentClassFormFields } from "features/commitment-classes/setup/CommitmentClassForm";
import { useInvestorClassCreate } from "gen/endpoints/investor-class/investor-class";
import { InvestorClassReadData } from "gen/model/investorClassReadData";
import { investorClassCreateBody } from "gen/schemas/investor-class/investor-class";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { useBoolean } from "usehooks-ts";

interface CreateCommitmentClassModalProps {
  onSuccess(): void;

  onCloseComplete(): void;

  fundFamilyId: string;
}

export const CreateCommitmentClassModal: React.FC<
  CreateCommitmentClassModalProps
> = ({ onSuccess, onCloseComplete, fundFamilyId }) => {
  const { t } = useTranslation(["commitmentClass", "common"]);

  const { mutateAsync: createCommitmentClass } = useInvestorClassCreate();

  const { value: isOpen, setFalse: close } = useBoolean(true);

  const form = useForm<InvestorClassReadData>({
    resolver: zodResolver(investorClassCreateBody),
    defaultValues: {
      fund_family_id: fundFamilyId,
      name: "",
      notes: "",
    },
  });

  const onSubmit = async (data: InvestorClassReadData) => {
    await createCommitmentClass({ fundFamilyId, data });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={() => {
            onSuccess();
            close();
          }}
        >
          <ModalHeader>{t("commitmentClass:createModal.title")}</ModalHeader>
          <ModalBody>
            <>
              <Box marginBottom={4}>
                <Text>{t("commitmentClass:createModal.description")}</Text>
              </Box>
              <CommitmentClassFormFields />
            </>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={close} />}
              confirm={
                <FormSubmitButton>{t("common:continue")}</FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
